import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { INTEGRATION_LOGOS } from "../constants/logos";
import { IntegrationLogo } from "../shared/IntegrationLogo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// ── Types ────────────────────────────────────────────────────────────

interface FlowingSegment {
  text: string;
  highlight?: boolean;
  color?: string;
}

interface Industry {
  id: string;
  label: string;
  color: string;
  bgColor: string;
  image: string;
  flowingText: FlowingSegment[];
  cta: {
    description: string;
    buttonLabel: string;
  };
}

// ── Data ─────────────────────────────────────────────────────────────

const INDUSTRIES: Industry[] = [
  {
    id: "hospitality",
    label: "Gastro",
    color: "text-primary",
    bgColor: "bg-primary",
    image: "/placeholder-restaurant.jpg",
    flowingText: [
      { text: "Full financial clarity", highlight: true, color: "text-primary" },
      { text: " - track revenue, costs & margins in real time." },
      { text: "\n" },
      { text: "2x repeat customers", highlight: true, color: "text-grow" },
      { text: " with digital loyalty passes." },
      { text: "\n" },
      { text: "One dashboard", highlight: true, color: "text-secondary" },
      { text: " for your entire team - shifts, payroll, and CRM." },
    ],
    cta: {
      description:
        "See how a restaurant chain streamlined operations and gained full financial clarity.",
      buttonLabel: "Read case study",
    },
  },
  {
    id: "services",
    label: "Services",
    color: "text-grow",
    bgColor: "bg-grow",
    image: "/placeholder-service.jpg",
    flowingText: [
      { text: "White-label booking & website", highlight: true, color: "text-primary" },
      { text: " - fully branded and integrated for that premium feel of your service." },
      { text: "\n" },
      { text: "5-star reviews on autopilot", highlight: true, color: "text-grow" },
      { text: " - build credibility with smart review funnels." },
      { text: "\n" },
      { text: "AI call reception, emailing & automation", highlight: true, color: "text-secondary" },
      { text: " - never miss a lead again." },
    ],
    cta: {
      description:
        "Learn how a salon grew bookings and built a 5-star reputation online.",
      buttonLabel: "Read case study",
    },
  },
  {
    id: "retail",
    label: "Retail",
    color: "text-secondary",
    bgColor: "bg-secondary",
    image: "/placeholder-retail.jpg",
    flowingText: [
      { text: "Real-time data across every location", highlight: true, color: "text-primary" },
      { text: " - inventory, sales, and employee performance in one view." },
      { text: "\n" },
      { text: "AI-powered decisions", highlight: true, color: "text-grow" },
      { text: " based on actual data and market research, not gut feeling." },
      { text: "\n" },
      { text: "Unified inventory & staff", highlight: true, color: "text-secondary" },
      { text: " - share stock and schedule employees across all your stores." },
    ],
    cta: {
      description:
        "Discover how a multi-store retailer unified data and made smarter decisions with AI.",
      buttonLabel: "Read case study",
    },
  },
];

// ── Mobile slide animation ──────────────────────────────────────────

const SLIDE_OFFSET = 60;
const contentVariants = {
  enter: (dir: number) => ({ x: dir * SLIDE_OFFSET, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir * -SLIDE_OFFSET, opacity: 0 }),
};
const contentTransition = {
  x: { type: "spring" as const, stiffness: 350, damping: 30, mass: 0.8 },
  opacity: { duration: 0.2, ease: "easeOut" },
};

// ── Flowing text renderer ────────────────────────────────────────────

function FlowingText({ segments }: { segments: FlowingSegment[] }) {
  return (
    <p className="text-base md:text-xl lg:text-2xl leading-relaxed text-foreground">
      {segments.map((seg, i) =>
        seg.text === "\n" ? (
          <br key={i} />
        ) : seg.highlight ? (
          <span key={i} className={`font-bold ${seg.color || "text-foreground"}`}>
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        )
      )}
    </p>
  );
}

// ── Integration carousel (lives inside hero mt-auto) ────────────────

export const IntegrationCarouselBar = () => {
  return (
    <>
      {/* Mobile: Static Grid */}
      <section className="md:hidden px-5 pt-6 pb-8 bg-background border-t border-b border-border">
        <p className="text-xs font-medium tracking-widest uppercase text-muted-foreground mb-6 text-center">
          Integrations
        </p>
        <div className="grid grid-cols-3 gap-x-4 gap-y-6">
          {INTEGRATION_LOGOS.map((logo, index) => (
            <div key={index} className="flex items-center justify-center h-12 w-full">
              {logo.label ? (
                <div className="flex items-center gap-1.5">
                  <img
                    src={Array.isArray(logo.src) ? logo.src[0] : logo.src}
                    alt={logo.alt}
                    className={`w-auto object-contain opacity-60 grayscale ${logo.mobileClasses || "max-h-10 max-w-[80px]"} ${logo.additionalClasses || ""}`}
                  />
                  <span
                    className="text-xs font-light text-muted-foreground whitespace-nowrap opacity-60"
                    style={logo.labelFont ? { fontFamily: logo.labelFont } : undefined}
                  >
                    {logo.label}
                  </span>
                </div>
              ) : (
                <img
                  src={Array.isArray(logo.src) ? logo.src[1] || logo.src[0] : logo.src}
                  alt={logo.alt}
                  className={`w-auto object-contain opacity-60 grayscale ${logo.mobileClasses || "max-h-10 max-w-[80px]"} ${logo.additionalClasses || ""}`}
                />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Desktop: Infinite Scroll Carousel */}
      <div className="hidden md:block w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] border-t border-b border-muted-foreground/10 py-4 overflow-hidden bg-background">
        <div className="absolute left-0 top-0 bottom-0 w-[10%] bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-[10%] bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        <motion.div
          className="flex animate-infinite-scroll hover:animation-paused motion-reduce:animate-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 1.85, ease: "easeOut" }}
        >
          {[0, 1].map((set) => (
            <div key={set} className="flex items-center shrink-0">
              {INTEGRATION_LOGOS.map((logo, index) => (
                <div key={`${set}-${index}`} className="mx-6 shrink-0">
                  <IntegrationLogo
                    src={logo.src}
                    alt={logo.alt}
                    label={logo.label}
                    labelFont={logo.labelFont}
                    additionalClasses={logo.additionalClasses}
                    height="h-9"
                  />
                </div>
              ))}
            </div>
          ))}
        </motion.div>
      </div>
    </>
  );
};

// ── Industry trust section (separate section below hero) ────────────

export const IndustryTrustSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [direction, setDirection] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeIndustry = INDUSTRIES[activeTab];

  const AUTO_ROTATE_MS = 5000;

  const startAutoRotate = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveTab((prev) => {
        const next = (prev + 1) % INDUSTRIES.length;
        setDirection(1);
        return next;
      });
    }, AUTO_ROTATE_MS);
  }, []);

  useEffect(() => {
    startAutoRotate();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startAutoRotate]);

  const handleTabChange = useCallback((newIndex: number) => {
    setDirection(newIndex > activeTab ? 1 : -1);
    setActiveTab(newIndex);
    startAutoRotate();
  }, [activeTab, startAutoRotate]);

  return (
    <section className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-background border-b border-border">
      {/* Desktop / Tablet */}
      <div className="hidden md:grid grid-cols-[minmax(0,30%)_1px_1fr_1px_240px] min-[1200px]:grid-cols-[minmax(0,30%)_1px_auto_1px_1fr_1px_240px] min-h-[300px]">
        {/* Photo + horizontal pills below (md–lg) or photo only (lg+) */}
        <div className="flex flex-col">
          <div className="overflow-hidden relative flex-1">
            {INDUSTRIES.map((industry) => (
              <img
                key={industry.image}
                src={industry.image}
                alt={industry.label}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                  industry.id === activeIndustry.id ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}
          </div>
          {/* Horizontal pills — tablet only */}
          <div className="flex min-[1200px]:hidden justify-center gap-2 px-3 py-2.5 border-t border-border">
            {INDUSTRIES.map((industry, i) => (
              <Button
                key={industry.id}
                onClick={() => handleTabChange(i)}
                className={`
                  relative overflow-hidden px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap
                  ${i === activeTab
                    ? "border border-input bg-primary/15 dark:bg-primary/25 font-medium text-sidebar-accent-foreground"
                    : "border border-input bg-background text-muted-foreground hover:bg-primary/10 dark:hover:bg-primary/15 hover:text-sidebar-accent-foreground"
                  }
                `}
              >
                {i === activeTab && (
                  <motion.div
                    key={`tablet-progress-${activeTab}`}
                    className="absolute inset-y-0 left-0 bg-primary/10 dark:bg-primary/20 rounded-full"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: AUTO_ROTATE_MS / 1000, ease: "linear" }}
                  />
                )}
                <span className="relative z-calendar-base">{industry.label}</span>
              </Button>
            ))}
          </div>
        </div>
        <div className="bg-border" />

        {/* Vertical pills — desktop only */}
        <div className="hidden min-[1200px]:flex flex-col justify-center gap-2 px-5">
          {INDUSTRIES.map((industry, i) => (
            <Button
              key={industry.id}
              onClick={() => handleTabChange(i)}
              className={`
                relative overflow-hidden w-full px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap
                ${i === activeTab
                  ? "border border-input bg-primary/15 dark:bg-primary/25 font-medium text-sidebar-accent-foreground"
                  : "border border-input bg-background text-muted-foreground hover:bg-primary/10 dark:hover:bg-primary/15 hover:text-sidebar-accent-foreground"
                }
              `}
            >
              {i === activeTab && (
                <motion.div
                  key={`progress-${activeTab}`}
                  className="absolute inset-y-0 left-0 bg-primary/10 dark:bg-primary/20 rounded-full"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: AUTO_ROTATE_MS / 1000, ease: "linear" }}
                />
              )}
              <span className="relative z-calendar-base">{industry.label}</span>
            </Button>
          ))}
        </div>
        <div className="hidden min-[1200px]:block bg-border" />

        {/* Flowing text — all stacked in same grid cell so tallest sets height */}
        <div className="grid px-8 py-6">
          {INDUSTRIES.map((industry) => (
            <div
              key={industry.id}
              className={`col-start-1 row-start-1 flex items-center transition-opacity duration-200 ${
                industry.id === activeIndustry.id ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            >
              <FlowingText segments={industry.flowingText} />
            </div>
          ))}
        </div>
        <div className="bg-border" />

        {/* CTA */}
        <div className="flex flex-col px-6 py-6 overflow-hidden">
          <div className="flex-1 grid">
            {INDUSTRIES.map((industry) => (
              <p
                key={industry.id}
                className={`col-start-1 row-start-1 text-sm text-muted-foreground leading-relaxed transition-opacity duration-200 ${
                  industry.id === activeIndustry.id ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
              >
                {industry.cta.description}
              </p>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-4"
            onClick={() => toast("Coming soon", { description: "Case studies are on the way!" })}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={activeIndustry.id + "-btn"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {activeIndustry.cta.buttonLabel}
              </motion.span>
            </AnimatePresence>
          </Button>
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden flex flex-col">
        {/* Photo + overlaid segmented control */}
        <div className="h-56 overflow-hidden border-b border-border relative">
          <AnimatePresence mode="popLayout" custom={direction} initial={false}>
            <motion.img
              key={activeIndustry.image}
              src={activeIndustry.image}
              alt={activeIndustry.label}
              className="absolute inset-0 w-full h-full object-cover"
              custom={direction}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={contentTransition}
            />
          </AnimatePresence>

          {/* Bottom gradient for pill readability */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />

          {/* Segmented control — overlaid at bottom of image */}
          <div className="absolute inset-x-0 bottom-3 flex justify-center z-10">
            <LayoutGroup id="industry-tabs">
              <div
                className="inline-flex bg-black/30 backdrop-blur-md rounded-full p-1 gap-0.5"
                role="tablist"
                aria-label="Industry selector"
                onKeyDown={(e) => {
                  const count = INDUSTRIES.length;
                  let next = activeTab;
                  if (e.key === "ArrowRight") next = (activeTab + 1) % count;
                  else if (e.key === "ArrowLeft") next = (activeTab - 1 + count) % count;
                  else return;
                  e.preventDefault();
                  handleTabChange(next);
                  requestAnimationFrame(() => tabRefs.current[next]?.focus());
                }}
              >
                {INDUSTRIES.map((industry, i) => (
                  <Button
                    key={industry.id}
                    ref={(el) => { tabRefs.current[i] = el; }}
                    role="tab"
                    aria-selected={i === activeTab}
                    aria-controls={`industry-panel-${industry.id}`}
                    id={`industry-tab-${industry.id}`}
                    tabIndex={i === activeTab ? 0 : -1}
                    onClick={() => handleTabChange(i)}
                    className="relative px-5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 z-calendar-base"
                  >
                    {i === activeTab && (
                      <motion.div
                        layoutId="industry-segment-indicator"
                        className="absolute inset-0 rounded-full shadow-sm overflow-hidden"
                        transition={{ type: "spring", stiffness: 500, damping: 35 }}
                      >
                        <div className="absolute inset-0 bg-white/90" />
                        <motion.div
                          key={`mobile-progress-${activeTab}`}
                          className="absolute inset-y-0 left-0 bg-black/10 rounded-full"
                          initial={{ width: "0%" }}
                          animate={{ width: "100%" }}
                          transition={{ duration: AUTO_ROTATE_MS / 1000, ease: "linear" }}
                        />
                      </motion.div>
                    )}
                    <span className={`relative z-calendar-base ${i === activeTab ? "text-foreground" : "text-white/80"}`}>
                      {industry.label}
                    </span>
                  </Button>
                ))}
              </div>
            </LayoutGroup>
          </div>
        </div>

        {/* Flowing text — directional slide */}
        <div className="px-6 py-6 border-b border-border overflow-hidden" style={{ minHeight: 140 }}>
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <motion.div
              key={activeIndustry.id + "-text"}
              custom={direction}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={contentTransition}
              role="tabpanel"
              id={`industry-panel-${activeIndustry.id}`}
              aria-labelledby={`industry-tab-${activeIndustry.id}`}
            >
              <FlowingText segments={activeIndustry.flowingText} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* CTA — directional slide description + static button */}
        <div className="px-6 py-5 grid grid-cols-1 min-[475px]:grid-cols-[1fr_auto] gap-4 items-center">
          <div className="overflow-hidden" style={{ minHeight: 40 }}>
            <AnimatePresence mode="wait" custom={direction} initial={false}>
              <motion.p
                key={activeIndustry.id + "-cta"}
                custom={direction}
                variants={contentVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={contentTransition}
                className="text-sm text-muted-foreground leading-relaxed"
              >
                {activeIndustry.cta.description}
              </motion.p>
            </AnimatePresence>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="whitespace-nowrap"
            onClick={() => toast("Coming soon", { description: "Case studies are on the way!" })}
          >
            {activeIndustry.cta.buttonLabel}
          </Button>
        </div>
      </div>
    </section>
  );
};
